import { FIT_DEFAULT } from '../config/videoSettings';
import { findLayoutRelatedElements } from './stackedLayout';

import type { ElementState } from '../renderer/ElementState';
import type { LayoutElementsResult } from './stackedLayout';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';
import type { ExtraElementData, Video } from '@src/types.ts/story';

const FIELDS_TO_NOT_COMPARE = [
  'time',
  'track',
  'duration',
  'trim_start',
  'color_overlay',
  'animations',
];

const FIELDS_POSITIONAL = [
  'x',
  'y',
  'width',
  'height',
  'x_anchor',
  'x_alignment',
  'y_anchor',
  'y_alignment',
];

const SOURCE_FIELDS_DEFAULTS: Record<string, string> = {
  fit: FIT_DEFAULT,
  x: '50%',
  y: '50%',
  width: '100%',
  height: '100%',
  x_alignment: '50%',
  y_alignment: '50%',
  x_anchor: '50%',
  y_anchor: '50%',
};

type FrameLockedElementState = ElementState & {
  extra?: Pick<LayoutElementsResult, 'isLayoutChild' | 'layoutType'> &
    Pick<ExtraElementData, 'layoutPosition'> & {
      parentTrack: number;
      isLayoutChildInner?: boolean;
    };
};

type SourceDiff = Record<
  string,
  {
    track: number;
    changes: Record<string, unknown>;
    extra?: FrameLockedElementState['extra'];
  }
>;

export type SourceDiffEntry = SourceDiff[string];

export const getOriginalVideoTrackSourceDiff = (
  videoCreator: VideoCreatorStore,
  prevTracksState: ElementState[],
  newTracksState: ElementState[],
): SourceDiff => {
  const diff: SourceDiff = {};
  collectFrameLockedElements(videoCreator, newTracksState).forEach(
    trackState => {
      const prevSource = findElementStateById(
        videoCreator,
        prevTracksState,
        trackState.source.id,
      )?.source;
      if (!prevSource) return;
      for (const key in { ...trackState.source, ...prevSource }) {
        if (FIELDS_TO_NOT_COMPARE.includes(key)) {
          continue;
        }

        if (prevSource && prevSource[key] !== trackState.source[key]) {
          diff[trackState.source.id] = diff[trackState.source.id] || {
            track: trackState.track,
            changes: {},
            extra: trackState.extra,
          };
          if (
            !Object.prototype.hasOwnProperty.call(trackState.source, key) &&
            SOURCE_FIELDS_DEFAULTS[key]
          ) {
            diff[trackState.source.id].changes[key] =
              SOURCE_FIELDS_DEFAULTS[key];
          } else {
            diff[trackState.source.id].changes[key] =
              trackState.source[key] || SOURCE_FIELDS_DEFAULTS[key] || null;
          }
        }
      }
    },
  );
  return diff;
};

/**
 * Can find inner elements of compositions
 */
export function findElementStateById(
  videoCreator: VideoCreatorStore,
  elements: ElementState[],
  targetId: string,
): ElementState | undefined {
  return (
    videoCreator.renderer?.findElement(
      elementState => elementState.source.id === targetId,
      { elements: elements, source: {} },
    ) ||
    // fallback to shallow find if renderer is not available
    elements.find(elementState => elementState.source.id === targetId)
  );
}

function collectFrameLockedElements(
  videoCreator: VideoCreatorStore,
  newTracksState: ElementState[],
): FrameLockedElementState[] {
  const extraElementData = videoCreator.currentVideo?.extraElementData || {};
  let result: FrameLockedElementState[] = [];
  newTracksState.forEach(trackState => {
    if (!videoCreator.frameLockedTracks.includes(trackState.track)) {
      return;
    }
    result.push(trackState);
    result = result.concat(
      // If frame locked element is in stacked layout, we need to take its
      // top/bottom children into account, as well as top/bottom children's
      // inner video elements
      includeStackedLayoutChildrenOf(
        trackState,
        newTracksState,
        extraElementData,
      ),
    );
  });
  return result;
}

function includeStackedLayoutChildrenOf(
  element: ElementState,
  allElements: ElementState[],
  extraElementData: Video['extraElementData'],
): FrameLockedElementState[] {
  const result: FrameLockedElementState[] = [];
  const parentLayoutInfo = findLayoutRelatedElements(
    undefined,
    element.source.id,
    extraElementData,
  );
  if (!parentLayoutInfo.isHiddenParent) {
    return [];
  }
  parentLayoutInfo.layoutChildrenIds.forEach(childId => {
    const childState = allElements.find(s => s.source.id === childId);
    if (!childState) {
      return;
    }
    const eed = extraElementData?.[childId];
    if (!eed || !('layoutPosition' in eed)) {
      return;
    }
    result.push({
      ...childState,
      extra: {
        isLayoutChild: true,
        layoutType: parentLayoutInfo.layoutType,
        layoutPosition: eed.layoutPosition,
        parentTrack: element.track,
      },
    });
    const innerVideo = childState.elements?.find(
      e => e.source.type === 'video',
    );
    if (innerVideo) {
      result.push({
        ...innerVideo,
        extra: {
          isLayoutChild: true,
          isLayoutChildInner: true,
          layoutType: parentLayoutInfo.layoutType,
          layoutPosition: eed.layoutPosition,
          parentTrack: element.track,
        },
      });
    }
  });
  return result;
}

export const getElementPositionProperties = (
  time: number,
  element: ElementState,
) => {
  const positionProperties: Record<string, any> = {};
  let scaleFactor = 1;
  if (element.source.animations?.length) {
    const currentPlayheadTime = time;
    const animation = element.source.animations[0];
    // assumes animation is from start to end of element time
    // const animationStartTime = animation.time || 0 + (element.globalTime || 0);
    // const animationEndTime = animation.duration

    // assumes linear easing
    if (element.globalTime > currentPlayheadTime && animation.start_scale) {
      scaleFactor = parseFloat(animation.start_scale) / 100;
    } else if (
      element.globalTime + element.duration < currentPlayheadTime &&
      animation.end_scale
    ) {
      scaleFactor = parseFloat(animation.end_scale) / 100;
    } else if (animation.start_scale || animation.end_scale) {
      const scalePerSecond =
        (parseFloat(animation.end_scale || '100') -
          parseFloat(animation.start_scale || '100')) /
        element.duration;
      scaleFactor =
        (parseFloat(animation.start_scale || '100') +
          scalePerSecond * (currentPlayheadTime - element.globalTime)) /
        100;
    }
  }

  for (const key of FIELDS_POSITIONAL) {
    positionProperties[key] =
      element.source[key] || SOURCE_FIELDS_DEFAULTS[key] || null;
    if (
      (scaleFactor !== 1 && positionProperties[key] && key === 'width') ||
      key === 'height'
    ) {
      positionProperties[key] = `${
        parseFloat(positionProperties[key]) * scaleFactor
      }%`;
    }
  }
  return positionProperties;
};
